'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { Suspense, useEffect, useRef } from 'react';

import { identifyUser, trackPageView } from '@/app/lib/analytics';

/**
 * Central GA4 listener for the app. Renders nothing.
 *
 * Responsibilities:
 * 1. Fire a `page_view` on the initial load and on every client-side route
 *    change. Next.js App Router does NOT emit pageviews on soft navigations,
 *    and our gtag config sets `send_page_view: false`, so this component is the
 *    single source of pageview truth for both web and mobile.
 * 2. Attach a stable, non-PII `user_id` and role/team user-properties to the
 *    GA session once the user is authenticated, so flows can be attributed.
 *
 * Must be rendered inside <Suspense> because `useSearchParams` opts the tree
 * into client-side rendering during static generation.
 */
function AnalyticsListenerInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data: session, status } = useSession();
  const lastIdentified = useRef<string | null>(null);

  // Pageview on route change (path or query string).
  useEffect(() => {
    if (!pathname) return;
    const query = searchParams?.toString();
    const url = query ? `${pathname}?${query}` : pathname;
    trackPageView(url);
  }, [pathname, searchParams]);

  // Keep the GA identity in sync with the session. Re-identify whenever the
  // identity tuple (user + role + team) changes so a team/role switch is
  // reflected, and clear the user_id on logout so later anonymous activity
  // isn't attributed to the previous user.
  useEffect(() => {
    if (status === 'unauthenticated') {
      if (lastIdentified.current !== null) {
        lastIdentified.current = null;
        identifyUser(null);
      }
      return;
    }
    if (status !== 'authenticated') return;
    const user = session?.user as
      | { id?: string | number; activeRole?: string; activeTeamId?: string | number }
      | undefined;
    const userId = user?.id != null ? String(user.id) : null;
    if (!userId) return;
    const role = user?.activeRole ?? undefined;
    const teamId =
      user?.activeTeamId != null ? String(user.activeTeamId) : undefined;
    const signature = `${userId}|${role ?? ''}|${teamId ?? ''}`;
    if (lastIdentified.current === signature) return;
    lastIdentified.current = signature;
    identifyUser(userId, { role, team_id: teamId });
  }, [status, session]);

  return null;
}

export default function AnalyticsListener() {
  return (
    <Suspense fallback={null}>
      <AnalyticsListenerInner />
    </Suspense>
  );
}
